import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Popconfirm, Card, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined } from '@ant-design/icons';
import {
  fetchExpenseTypes,
  createExpenseType,
  updateExpenseType,
  deleteExpenseType,
  type ExpenseTypeDto
} from '../services/cashService';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

const ExpenseTypes: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [items, setItems] = useState<ExpenseTypeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ExpenseTypeDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchExpenseTypes();
      setItems(data);
    } catch (error) {
      message.error('Gider kalemleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (item: ExpenseTypeDto) => {
    setEditingItem(item);
    form.setFieldsValue({
      typeCode: item.typeCode,
      typeName: item.typeName,
      description: item.description ?? '',
      isActive: item.isActive
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        typeCode: values.typeCode?.trim() ?? '',
        typeName: values.typeName?.trim() ?? '',
        description: values.description?.trim() || undefined,
        isActive: values.isActive !== undefined ? values.isActive : true
      };

      if (editingItem) {
        await updateExpenseType(editingItem.id, payload);
        message.success('Gider kalemi güncellendi');
      } else {
        await createExpenseType(payload);
        message.success('Gider kalemi eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      loadItems();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteExpenseType(id);
      message.success('Gider kalemi silindi');
      loadItems();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Gider kalemi silinirken hata oluştu');
    }
  };

  const columns = [
    {
      title: 'Kod',
      dataIndex: 'typeCode',
      key: 'typeCode',
      width: 140,
    },
    {
      title: 'Gider Tipi Adı',
      dataIndex: 'typeName',
      key: 'typeName',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 180,
      render: (_: any, record: ExpenseTypeDto) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu gider kalemini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>
          <DollarOutlined /> Gider Kalemleri
        </h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Gider Kalemi Ekle
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 450 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} gider kalemi`,
          }}
        />
      </Card>

      <Modal
        title={editingItem ? 'Gider Kalemini Düzenle' : 'Yeni Gider Kalemi Ekle'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 520}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="typeCode"
            label="Kod"
            rules={[{ required: true, message: 'Lütfen kod girin' }]}
          >
            <Input placeholder="Örn: GENEL_GIDER, HIZMET" />
          </Form.Item>
          <Form.Item
            name="typeName"
            label="Gider Tipi Adı"
            rules={[{ required: true, message: 'Lütfen gider tipi adı girin' }]}
          >
            <Input placeholder="Örn: Genel Gider, Hizmet Gideri" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea rows={2} placeholder="İsteğe bağlı açıklama" />
          </Form.Item>
          {editingItem && (
            <Form.Item
              name="isActive"
              label="Aktif"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ExpenseTypes;
